import React, { useState, useMemo, useEffect } from 'react';
import GlassSelect from './GlassSelect';

const SIGNAL_TYPES = [
  'hiring_activity',
  'news_summary',
  'customer_complaints',
  'leadership_change',
  'transformation_announcement',
  'platform_migration',
  'vendor_activity',
  'social_sentiment',
  'release_activity',
  'investment_funding',
  'ma_ownership',
  'corporate_filing',
  'corporate_change',
  'company_profile',
];

function confidenceBadge(score) {
  const pct = Math.round(score * 100);
  const cls = pct >= 75 ? 'badge-high' : pct >= 50 ? 'badge-medium' : 'badge-low';
  return <span className={`badge ${cls}`}>{pct}%</span>;
}

function formatSignalType(type) {
  return type.replace(/_/g, ' ');
}

export default function SignalReview({ candidates, companyName, onApprove, onReject, loading, lowSignalMode, verificationStatus, duplicatesSuppressed, analysisLens, onLensChange }) {
  const [selected, setSelected] = useState(new Set());
  const [editedTypes, setEditedTypes] = useState({});
  const [minConfidence, setMinConfidence] = useState(0);
  const [minRelevance, setMinRelevance] = useState(0);
  const [minTrust, setMinTrust] = useState(0);
  const [expandedSnippets, setExpandedSnippets] = useState(new Set());

  useEffect(() => {
    const retainedIds = new Set(candidates.map(candidate => candidate.id));
    setSelected(current => new Set([...current].filter(id => retainedIds.has(id))));
  }, [candidates]);

  // Apply filters
  const filtered = useMemo(() => {
    return candidates.filter(c => {
      if (c.confidence_score < minConfidence) return false;
      if ((c.relevance_score || 0) < minRelevance) return false;
      if ((c.source_trust_score || 0) < minTrust) return false;
      return true;
    });
  }, [candidates, minConfidence, minRelevance, minTrust]);

  const allSelected = selected.size === filtered.length && filtered.length > 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(c => c.id)));
    }
  };

  const toggleRow = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleTypeChange = (id, newType) => {
    setEditedTypes(prev => ({
      ...prev,
      [id]: { inferred_signal_type: newType },
    }));
  };

  const handleBulkApprove = () => {
    setSelected(new Set(filtered.map(c => c.id)));
  };

  const handleBulkReject = () => {
    setSelected(new Set());
  };

  const handleIngest = () => {
    if (selected.size === 0) return;
    const selectedIds = Array.from(selected);
    onApprove(selectedIds, editedTypes);
  };

  const getDisplayType = (candidate) => {
    if (editedTypes[candidate.id]?.inferred_signal_type) {
      return editedTypes[candidate.id].inferred_signal_type;
    }
    return candidate.inferred_signal_type;
  };

  const toggleSnippet = (id) => {
    setExpandedSnippets(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedCount = selected.size;

  // Stats
  const typeBreakdown = useMemo(() => {
    const breakdown = {};
    filtered.forEach(c => {
      const t = getDisplayType(c);
      breakdown[t] = (breakdown[t] || 0) + 1;
    });
    return breakdown;
  }, [filtered, editedTypes]);

  const hiddenCount = candidates.length - filtered.length;

  return (
    <div>
      {/* Verification status banner */}
      {verificationStatus && verificationStatus !== 'verified' && (
        <div className="panel" style={{ marginBottom: '8px', borderLeft: `3px solid ${
          verificationStatus === 'low_evidence' || verificationStatus === 'needs_review' ? 'var(--danger)' :
          verificationStatus === 'single_source' ? 'var(--warning)' :
          'var(--primary)'
        }` }}>
          <div className="panel-body" style={{ padding: '8px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="verification-status-banner">
            <span className={`badge ${
              verificationStatus === 'low_evidence' || verificationStatus === 'needs_review' ? 'badge-low' :
              verificationStatus === 'single_source' ? 'badge-medium' :
              verificationStatus === 'corroborated' ? 'badge-high' : 'badge-type'
            }`} style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}
              data-testid="verification-status-badge"
            >
              {verificationStatus.replace(/_/g, ' ')}
            </span>
            <span style={{ color: 'var(--text-secondary)' }}>
              {verificationStatus === 'low_evidence' && 'Limited evidence — findings are preliminary indicators.'}
              {verificationStatus === 'single_source' && 'All evidence from a single source — corroboration recommended.'}
              {verificationStatus === 'partial' && 'Partial corroboration — key findings should be validated.'}
              {verificationStatus === 'needs_review' && 'Evidence quality could not be assessed.'}
              {verificationStatus === 'corroborated' && 'Findings corroborated by multiple independent sources.'}
            </span>
            {duplicatesSuppressed > 0 && (
              <span style={{ color: 'var(--text-secondary)', marginLeft: 'auto', fontSize: '11px' }}
                data-testid="duplicates-suppressed-count">
                {duplicatesSuppressed} duplicate(s) suppressed
              </span>
            )}
          </div>
        </div>
      )}
      {verificationStatus === 'verified' && (
        <div className="panel" style={{ marginBottom: '8px', borderLeft: '3px solid var(--success)' }}>
          <div className="panel-body" style={{ padding: '8px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="verification-status-banner">
            <span className="badge badge-high" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}
              data-testid="verification-status-badge">verified</span>
            <span style={{ color: 'var(--text-secondary)' }}>Strong corroboration from multiple independent sources.</span>
          </div>
        </div>
      )}
      {/* Low-signal mode indicator */}
      {lowSignalMode && (
        <div className="panel" style={{ marginBottom: '8px', borderLeft: '3px solid var(--warning)' }}>
          <div className="panel-body" style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--text-secondary)' }} data-testid="low-signal-banner">
            Low external signal volume detected — analysis enriched using company profile and corporate record evidence.
          </div>
        </div>
      )}
      {/* Stats bar */}
      <div className="panel" style={{ marginBottom: '8px' }}>
        <div className="panel-body" style={{ padding: '10px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', fontWeight: '600' }} data-testid="candidate-count">
                {filtered.length} candidate signals
              </span>
              {hiddenCount > 0 && (
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }} data-testid="hidden-count">
                  ({hiddenCount} hidden by filters)
                </span>
              )}
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                for {companyName}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {Object.entries(typeBreakdown).map(([type, count]) => (
                <span key={type} className="badge badge-type">{formatSignalType(type)}: {count}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quality Filters */}
      <div className="panel" style={{ marginBottom: '8px' }}>
        <div className="panel-body" style={{ padding: '8px 16px' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>Quality Filters</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <label style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Min Confidence</label>
              <input type="range" min="0" max="100" value={minConfidence * 100} onChange={e => setMinConfidence(Number(e.target.value) / 100)}
                style={{ width: '80px' }} data-testid="filter-min-confidence" />
              <span style={{ fontSize: '11px', fontFamily: "'IBM Plex Mono', monospace", width: '32px' }}>{Math.round(minConfidence * 100)}%</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <label style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Min Relevance</label>
              <input type="range" min="0" max="100" value={minRelevance * 100} onChange={e => setMinRelevance(Number(e.target.value) / 100)}
                style={{ width: '80px' }} data-testid="filter-min-relevance" />
              <span style={{ fontSize: '11px', fontFamily: "'IBM Plex Mono', monospace", width: '32px' }}>{Math.round(minRelevance * 100)}%</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <label style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Min Trust</label>
              <input type="range" min="0" max="100" value={minTrust * 100} onChange={e => setMinTrust(Number(e.target.value) / 100)}
                style={{ width: '80px' }} data-testid="filter-min-trust" />
              <span style={{ fontSize: '11px', fontFamily: "'IBM Plex Mono', monospace", width: '32px' }}>{Math.round(minTrust * 100)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <button
          className="btn btn-outline btn-sm"
          onClick={handleBulkApprove}
          disabled={loading}
          data-testid="bulk-approve-button"
        >
          Select All
        </button>
        <button
          className="btn btn-outline btn-sm"
          onClick={handleBulkReject}
          disabled={loading}
          data-testid="bulk-reject-button"
        >
          Deselect All
        </button>
        {onReject && <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => onReject(Array.from(selected))}
          disabled={loading || selected.size === 0}
          data-testid="reject-selected-button"
        >Reject selected</button>}
        <span className="toolbar-count" data-testid="selected-count">
          {selectedCount} selected
        </span>
      </div>

      {/* Table */}
      <div className="panel" style={{ overflow: 'auto' }}>
        <div className="data-table-wrap">
        <table className="data-table" data-testid="signals-table">
          <thead>
            <tr>
              <th className="checkbox-cell">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  data-testid="select-all-checkbox"
                />
              </th>
              <th>Source</th>
              <th>Title</th>
              <th>Snippet</th>
              <th>Signal Type</th>
              <th title="Likelihood the signal classification is correct">Confidence</th>
              <th title="How strongly the signal matches the target organisation and scope">Relevance</th>
              <th title="Credibility of the source — authoritative outlets score higher">Trust</th>
              <th title="Whether similar evidence exists across multiple independent sources">Corroboration</th>
              <th title="Whether the signal matches at organisation, vector, or sub-vector level">Scope</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`signal-row-${c.id}`}>
                <td className="checkbox-cell">
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    disabled={loading}
                    onChange={() => toggleRow(c.id)}
                    data-testid={`signal-checkbox-${c.id}`}
                  />
                </td>
                <td>
                  <div style={{ fontSize: '12px', fontWeight: '500' }}>{c.source_name}</div>
                  {c.source_url && (
                    <a
                      href={c.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '10px', color: 'var(--primary)', wordBreak: 'break-all' }}
                    >
                      link
                    </a>
                  )}
                </td>
                <td style={{ fontWeight: '500', maxWidth: '250px' }}>{c.title}</td>
                <td>
                  <div
                    onClick={() => toggleSnippet(c.id)}
                    style={{ cursor: 'pointer', fontSize: '12px', maxWidth: '320px', position: 'relative' }}
                    data-testid={`snippet-toggle-${c.id}`}
                  >
                    {expandedSnippets.has(c.id) ? (
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.5', color: 'var(--text)' }}>
                        {c.snippet}
                        <span style={{ display: 'block', fontSize: '10px', color: 'var(--primary)', marginTop: '4px', fontWeight: '500' }}>collapse</span>
                      </div>
                    ) : (
                      <div style={{ color: 'var(--text-secondary)' }}>
                        {c.snippet && c.snippet.length > 100 ? c.snippet.slice(0, 100) + '...' : c.snippet}
                        {c.snippet && c.snippet.length > 100 && (
                          <span style={{ fontSize: '10px', color: 'var(--primary)', marginLeft: '4px', fontWeight: '500' }}>more</span>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Match rationale + contribution hints */}
                  {c.metadata?.match_rationale && (
                    <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '4px', lineHeight: '1.4' }}
                      data-testid={`match-rationale-${c.id}`}>
                      <span style={{ fontWeight: '600' }}>Why: </span>{c.metadata.match_rationale}
                    </div>
                  )}
                  {c.metadata?.contribution_hints && c.metadata.contribution_hints.length > 0 && (
                    <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', marginTop: '3px' }}
                      data-testid={`contribution-hints-${c.id}`}>
                      {c.metadata.contribution_hints.map((h, idx) => (
                        <span key={idx} className="badge badge-type" style={{ fontSize: '9px', padding: '1px 5px' }}>{h}</span>
                      ))}
                    </div>
                  )}
                  {/* Sector profile reasons — additive, only when present */}
                  {c.metadata?.profile_sector && (
                    (c.metadata.profile_commercial_reasons?.length > 0
                      || c.metadata.profile_risk_reasons?.length > 0
                      || c.metadata.profile_weak_noise_flag) && (
                      <div
                        style={{ marginTop: '4px', fontSize: '10px', color: 'var(--text-secondary)', lineHeight: '1.4' }}
                        data-testid={`sector-reasons-${c.id}`}
                        title={`Why this matters in ${c.metadata.profile_sector}`}
                      >
                        <span style={{ fontWeight: 600 }}>
                          Why this matters in {c.metadata.profile_sector}:
                        </span>
                        <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', marginTop: '3px' }}>
                          {(c.metadata.profile_commercial_reasons || []).map((r, idx) => (
                            <span
                              key={`c-${idx}`}
                              className="badge badge-type"
                              style={{ fontSize: '9px', padding: '1px 5px' }}
                              data-testid={`sector-comm-${c.id}-${idx}`}
                            >
                              {r}
                            </span>
                          ))}
                          {(c.metadata.profile_risk_reasons || []).map((r, idx) => (
                            <span
                              key={`r-${idx}`}
                              className="badge badge-low"
                              style={{ fontSize: '9px', padding: '1px 5px' }}
                              data-testid={`sector-risk-${c.id}-${idx}`}
                            >
                              {r}
                            </span>
                          ))}
                          {c.metadata.profile_weak_noise_flag && (
                            <span
                              className="badge"
                              style={{
                                fontSize: '9px',
                                padding: '1px 5px',
                                background: 'var(--surface)',
                                color: 'var(--text-secondary)',
                                border: '1px solid var(--border)',
                              }}
                              title="Phrase matched a weak / noisy pattern in this sector — review carefully"
                              data-testid={`sector-weak-noise-${c.id}`}
                            >
                              Weak / noisy match
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  )}
                </td>
                <td>
                  <GlassSelect
                    className="signal-type-select"
                    value={getDisplayType(c)}
                    onChange={(e) => handleTypeChange(c.id, e.target.value)}
                    data-testid={`signal-type-select-${c.id}`}
                  >
                    {SIGNAL_TYPES.map(t => (
                      <option key={t} value={t}>{formatSignalType(t)}</option>
                    ))}
                  </GlassSelect>
                </td>
                <td>{confidenceBadge(c.confidence_score)}</td>
                <td>{confidenceBadge(c.relevance_score || 0)}</td>
                <td>{confidenceBadge(c.source_trust_score || 0)}</td>
                <td>
                  <span className={`badge ${
                    c.metadata?.corroboration === 'clustered' ? 'badge-high' :
                    c.metadata?.corroboration === 'corroborated' ? 'badge-medium' :
                    'badge-low'
                  }`} style={{ fontSize: '10px' }}
                    data-testid={`corroboration-${c.id}`}>
                    {c.metadata?.corroboration || 'single'}
                  </span>
                </td>
                <td>
                  <span className={`badge ${(c.metadata?.scope_match === 'sub_vector') ? 'badge-low' : (c.metadata?.scope_match === 'vector') ? 'badge-medium' : 'badge-type'}`}
                    style={{ fontSize: '10px' }}>
                    {c.metadata?.scope_match || 'org'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Analysis Lens selector + Ingest button */}
      <div style={{ paddingTop: '12px' }}>
        <div className="panel" style={{ marginBottom: '8px' }}>
          <div className="panel-body" style={{ padding: '8px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
                Analysis Lens
              </span>
              <GlassSelect
                className="signal-type-select"
                value={analysisLens || ''}
                onChange={(e) => onLensChange && onLensChange(e.target.value)}
                style={{ padding: '5px 8px', fontSize: '12px', minWidth: '200px' }}
                data-testid="analysis-lens-select"
              >
                <option value="">Balanced (default)</option>
                <option value="commercial_opportunity">Commercial Opportunity</option>
                <option value="delivery_risk">Delivery Risk</option>
                <option value="operational_risk">Operational Risk</option>
                <option value="transformation_pressure">Transformation Pressure</option>
                <option value="platform_health">Platform / Product Health</option>
                <option value="optimisation">Optimisation / Hardening</option>
              </GlassSelect>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                {analysisLens === 'commercial_opportunity' && 'Focus on intervention potential, change triggers, and why this is worth pursuing'}
                {analysisLens === 'delivery_risk' && 'Focus on delivery friction, customer impact, and operational distress'}
                {analysisLens === 'operational_risk' && 'Focus on whether the organisation is in trouble, coping, or simply changing'}
                {analysisLens === 'transformation_pressure' && 'Focus on change signals, governance, and transformation readiness'}
                {analysisLens === 'platform_health' && 'Focus on vendor/platform stress and delivery friction'}
                {analysisLens === 'optimisation' && 'Focus on hardening opportunities in a stable environment'}
                {!analysisLens && 'Balanced view across all dimensions'}
              </span>
            </div>
          </div>
        </div>
        <button
          className="btn btn-success"
          onClick={handleIngest}
          disabled={selectedCount === 0 || loading}
          data-testid="ingest-approved-button"
          style={{ width: '100%' }}
        >
          {loading
            ? 'Ingesting & Analyzing...'
            : `Ingest ${selectedCount} Approved Signal${selectedCount !== 1 ? 's' : ''} & Run Analysis`}
        </button>
        {hiddenCount > 0 && (
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textAlign: 'center', marginTop: '4px' }}>
            {hiddenCount} low-quality signals hidden. Adjust filters above to reveal.
          </div>
        )}
      </div>
    </div>
  );
}
